import { logger } from "@/utils/logger";
import { supabase } from '@/integrations/supabase/client';

interface ErrorAlertData {
  dealerId: number;
  errorMessage: string;
  userMessage?: string;
  sessionId?: string;
  openaiSessionId?: string;
  stackTrace?: string;
  functionName?: string;
  timestamp?: string;
  statusCode?: number;
  responseBody?: any;
  requestPayload?: any;
}

/**
 * Service to send error alerts via email when critical errors occur
 */
export class ErrorAlertService {
  /**
   * Send an error alert email with detailed information
   */
  static async sendErrorAlert(data: ErrorAlertData): Promise<void> {
    try {
      logger.log('🚨 ErrorAlertService: Sending error alert with data:', {
        dealerId: data.dealerId,
        errorMessage: data.errorMessage.substring(0, 100) + '...',
        functionName: data.functionName,
        sessionId: data.sessionId
      });

      const { data: response, error } = await supabase.functions.invoke('send-error-alert', {
        body: {
          ...data,
          timestamp: data.timestamp || new Date().toISOString()
        }
      });

      if (error) {
        console.error('❌ Failed to send error alert:', error);
      } else {
      }
    } catch (error) {
      console.error('💥 Error invoking send-error-alert function:', error);
    }
  }

  /**
   * Utility method to send error alert from try-catch blocks
   */
  static async alertError(
    dealerId: number, 
    error: Error, 
    context?: {
      userMessage?: string;
      sessionId?: string;
      functionName?: string;
    }
  ): Promise<void> {
    await this.sendErrorAlert({
      dealerId,
      errorMessage: error.message,
      stackTrace: error.stack,
      userMessage: context?.userMessage,
      sessionId: context?.sessionId,
      functionName: context?.functionName
    });
  }

  /**
   * Utility method for API/Edge function errors
   */
  static async alertApiError(
    dealerId: number,
    errorMessage: string,
    context: {
      functionName: string;
      userMessage?: string;
      sessionId?: string;
      openaiSessionId?: string;
      statusCode?: number;
      responseBody?: any;
      requestPayload?: any;
      additionalInfo?: any;
    }
  ): Promise<void> {
    await this.sendErrorAlert({
      dealerId,
      errorMessage,
      functionName: context.functionName,
      userMessage: context.userMessage,
      sessionId: context.sessionId,
      openaiSessionId: context.openaiSessionId,
      statusCode: context.statusCode,
      responseBody: context.responseBody,
      requestPayload: context.requestPayload,
      stackTrace: context.additionalInfo ? JSON.stringify(context.additionalInfo, null, 2) : undefined
    });
  }
}